import {
	FILTER_CONTROL_HEIGHT_CLASS,
	FilterBar,
	FilterField,
	Input,
} from "@components";
import { SearchLg } from "@untitledui/icons";
import type { ReactNode } from "react";
import { useMemo, useRef } from "react";
import Select from "react-select";
import type { GroupResponse } from "@/shared/types";
import { COMMUNICATION_SELECT_STYLES } from "../constants";

export type CommunicationFiltersState = {
	search: string;
	groupIds: number[];
};

export function createDefaultCommunicationFilters(): CommunicationFiltersState {
	return {
		search: "",
		groupIds: [],
	};
}

type GroupOption = {
	value: string;
	label: string;
};

type CommunicationFiltersProps = {
	filters: CommunicationFiltersState;
	onFiltersChange: (filters: CommunicationFiltersState) => void;
	onPageChange: (page: number) => void;
	searchInput: string;
	onSearchInputChange: (value: string) => void;
	groups: GroupResponse[];
	actions?: ReactNode;
};

const SEARCH_INPUT_ID = "communication-search";

export function CommunicationFilters({
	filters,
	onFiltersChange,
	onPageChange,
	searchInput,
	onSearchInputChange,
	groups,
	actions,
}: CommunicationFiltersProps) {
	const filtersRef = useRef(filters);
	filtersRef.current = filters;

	const groupOptions = useMemo<GroupOption[]>(
		() =>
			groups.map((group) => ({
				value: String(group.id),
				label: group.name,
			})),
		[groups]
	);

	const selectedGroupOptions = useMemo(() => {
		const selected: GroupOption[] = [];

		for (const groupId of filters.groupIds) {
			const option = groupOptions.find(
				(item) => item.value === String(groupId)
			);
			if (option) {
				selected.push(option);
			}
		}

		return selected;
	}, [filters.groupIds, groupOptions]);

	return (
		<FilterBar actions={actions} label="Filtros de comunicação">
			<FilterField
				className="sm:max-w-[307px]"
				grow
				htmlFor={SEARCH_INPUT_ID}
				label="Pesquisar por placa"
				labelVisibility="hidden"
			>
				<div className="relative w-full">
					<SearchLg
						aria-hidden
						className="-translate-y-1/2 absolute top-1/2 left-3 size-5 text-quaternary"
					/>
					<Input
						className={`${FILTER_CONTROL_HEIGHT_CLASS} w-full pl-9`}
						id={SEARCH_INPUT_ID}
						onChange={(event) => {
							onSearchInputChange(event.target.value);
						}}
						placeholder="Pesquise por placa"
						value={searchInput}
					/>
				</div>
			</FilterField>

			<FilterField label="Grupo" labelVisibility="hidden">
				<Select
					aria-label="Selecione o grupo"
					className="w-full"
					isMulti
					menuPortalTarget={document.body}
					onChange={(options) => {
						const selected = options ?? [];
						const groupIds = selected
							.map((option) => Number(option.value))
							.filter((id) => !Number.isNaN(id));

						onFiltersChange({
							...filtersRef.current,
							groupIds,
						});
						onPageChange(1);
					}}
					options={groupOptions}
					placeholder="Selecione o grupo"
					styles={COMMUNICATION_SELECT_STYLES}
					value={selectedGroupOptions}
				/>
			</FilterField>
		</FilterBar>
	);
}
